import React from 'react'
import { ProgressSpinner } from '~/components/common/ProgressSpinner'

/**
 * Shared TodoList component used by both Chat and Task progress message views.
 * Displays a collapsible list of todo items with status icons.
 */

export interface TodoItem {
  todo: string
  status: 'TODO_PENDING' | 'TODO_IN_PROGRESS' | 'TODO_COMPLETED'
}

/** Icon for todo item status */
const TodoStatusIcon: React.FC<{ status: TodoItem['status'] }> = ({ status }) => {
  if (status === 'TODO_COMPLETED') {
    return (
      <div className='w-3 h-3 flex items-center justify-center rounded-full bg-[#846FE1]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='10'
          height='10'
          viewBox='0 0 10 10'
          fill='none'
        >
          <path
            d='M8 3L4 7L2 5'
            stroke='white'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
    )
  }

  if (status === 'TODO_IN_PROGRESS') {
    return (
      <div className='w-3 h-3 flex items-center justify-center'>
        <ProgressSpinner size='md' />
      </div>
    )
  }

  // TODO_PENDING - empty circle
  return (
    <div className='w-3 h-3 flex items-center justify-center'>
      <div className='w-[10px] h-[10px] rounded-full border-[1.25px] border-[rgba(0,0,0,0.25)]' />
    </div>
  )
}

/** Single todo item row */
const TodoItemRow: React.FC<{ item: TodoItem }> = ({ item }) => {
  const textClass =
    item.status === 'TODO_COMPLETED'
      ? 'text-[rgba(0,0,0,0.45)]'
      : item.status === 'TODO_IN_PROGRESS'
        ? 'text-[rgba(91,91,213,1)]'
        : 'text-[rgba(0,0,0,0.65)]'

  return (
    <div className='flex items-center self-stretch gap-[8px] py-[8px] px-[16px]'>
      <TodoStatusIcon status={item.status} />
      <span className={`font-sans text-[12px] leading-normal font-medium ${textClass}`}>
        {item.todo}
      </span>
    </div>
  )
}

interface TodoListProps {
  items: TodoItem[]
  /** Optional: control expanded state externally */
  defaultExpanded?: boolean
  /** If true, force all items to show as completed (used when task is done but agent didn't update todos) */
  forceComplete?: boolean
}

/** Collapsible todo list component */
export const TodoList: React.FC<TodoListProps> = ({
  items,
  defaultExpanded = true,
  forceComplete = false
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded)

  if (items.length === 0) return null

  // If forceComplete is true, treat all items as completed
  const effectiveItems = forceComplete
    ? items.map(item => ({ ...item, status: 'TODO_COMPLETED' as const }))
    : items

  const completedCount = effectiveItems.filter(i => i.status === 'TODO_COMPLETED').length
  const totalCount = effectiveItems.length

  return (
    <div className='flex flex-col items-start gap-[10px] self-stretch py-[8px]'>
      <div className='flex flex-col items-start self-stretch rounded-[24px] border border-[rgba(91,91,213,0.20)]'>
        {/* Expandable header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex flex-col justify-center items-start self-stretch gap-[4px] py-[4px] rounded-t-[24px] ${!isExpanded ? 'rounded-b-[24px]' : ''} bg-[#F7F7FF]`}
        >
          <div className='flex items-center gap-[8px] py-[8px] px-[16px] height-[36px]'>
            <svg
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
              viewBox='0 0 12 12'
              fill='none'
            >
              <g clipPath='url(#clip0_todo_list)'>
                <path
                  d='M10.0153 4.76552L6.26528 8.51552C6.23045 8.55039 6.18909 8.57805 6.14357 8.59692C6.09804 8.61579 6.04925 8.6255 5.99996 8.6255C5.95068 8.6255 5.90189 8.61579 5.85636 8.59692C5.81084 8.57805 5.76948 8.55039 5.73465 8.51552L1.98465 4.76552C1.91429 4.69516 1.87476 4.59972 1.87476 4.50021C1.87476 4.4007 1.91429 4.30526 1.98465 4.2349C2.05502 4.16453 2.15045 4.125 2.24996 4.125C2.34948 4.125 2.44491 4.16453 2.51528 4.2349L5.99996 7.72005L9.48465 4.2349C9.51949 4.20005 9.56086 4.17242 9.60638 4.15356C9.6519 4.1347 9.70069 4.125 9.74996 4.125C9.79924 4.125 9.84803 4.1347 9.89355 4.15356C9.93907 4.17242 9.98044 4.20005 10.0153 4.2349C10.0501 4.26974 10.0778 4.3111 10.0966 4.35662C10.1155 4.40214 10.1252 4.45094 10.1252 4.50021C10.1252 4.54948 10.1155 4.59827 10.0966 4.64379C10.0778 4.68932 10.0501 4.73068 10.0153 4.76552Z'
                  fill='black'
                  fillOpacity='0.45'
                />
              </g>
              <defs>
                <clipPath id='clip0_todo_list'>
                  <rect width='12' height='12' fill='white' />
                </clipPath>
              </defs>
            </svg>
            <div className='flex items-center gap-[4px]'>
              <span className='text-[12px] font-sans font-medium text-center leading-normal text-[#846FE1]'>
                To-Do List
              </span>
              <span className='text-[12px] font-sans font-medium text-center leading-normal text-[rgba(0,0,0,0.45)]'>
                · {completedCount} of {totalCount} complete
              </span>
            </div>
          </div>
        </button>

        {/* Todo items - collapsible */}
        {isExpanded && (
          <div className='flex flex-col items-start self-stretch py-[8px]'>
            {effectiveItems.map((item, idx) => (
              <TodoItemRow key={`todo-${idx}-${item.todo.substring(0, 20)}`} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TodoList
